import { noop } from "@aircall/tractor";
import { formatSecondsToDuration } from "@dashboard/library";

import { useToast } from "@aircall/tractor";
import { differenceInSeconds } from "date-fns";
import { useTranslation } from "react-i18next";

import i18n from "@/config/i18n.config";
import {
  categorizeSpeakers,
  deductMainSpeakers,
} from "@/helpers/participants/participants.helpers";
import { formatInternational } from "@/helpers/phone_number/phone_number.helpers";
import { formatSpeakerDisplay } from "@/helpers/speaker/speaker.helpers";
import { Call as CustomCall } from "@/states/call";
import { useCall } from "../useCall/useCall";

function download(filename: string, text: string) {
  const element = document.createElement("a");
  element.setAttribute(
    "href",
    `data:text/plain;charset=utf-8,${encodeURIComponent(text)}`
  );
  element.setAttribute("download", filename);
  element.style.display = "none";

  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
}

function parse(call: CustomCall, locale: string) {
  const formattedCreatedAt = new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
    timeStyle: "long",
  }).format(new Date(call.createdAt));

  let result = "";

  result += `${formatSpeakerDisplay([...(call.speakers?.values() ?? [])])}\n`;
  result += `${i18n.t("call_context.start_time")}: ${formattedCreatedAt}\n`;

  if (call.durationInSecond && call.answeredAt && call.createdAt) {
    result += `Total time: ${formatSecondsToDuration(
      call.durationInSecond +
        differenceInSeconds(new Date(call.answeredAt), new Date(call.createdAt))
    )}\n\n`;
  }

  if (call.from && call.to && call.speakers) {
    const { mainAgentId, mainExternalNumber } = deductMainSpeakers({
      type: call.type,
      from: call.from,
      to: call.to,
    });

    const { mainAgent, externalUser, otherAgents, otherUsers } =
      categorizeSpeakers(call.speakers, mainAgentId, mainExternalNumber);

    result += `${i18n.t("main_agent")}: ${mainAgent?.fullName ?? formatInternational(mainAgent?.number ?? "")}\n`;
    result += `${i18n.t("external_user")}: ${externalUser?.fullName ?? formatInternational(externalUser?.number ?? "")}\n`;

    if (otherAgents.length) {
      result += `${i18n.t("other_agents")}:\n`;
      otherAgents.forEach((agent) => {
        result += `${agent.fullName ?? formatInternational(agent.number ?? "")}\n`;
      });
    }

    if (otherUsers.length) {
      result += `${i18n.t("other_users")}:\n`;
      otherUsers.forEach((user) => {
        result += `${user.fullName ?? formatInternational(user.number ?? "")}\n`;
      });
    }
  }

  if (call.transcription?.content?.utterances?.length) {
    result += `\n${i18n.t("transcription")}:\n`;

    // Deduce main speakers for voicemails (when speakers map is not available)
    const mainSpeakers =
      call.from && call.to
        ? deductMainSpeakers({
            type: call.type,
            from: call.from,
            to: call.to,
          })
        : null;

    for (const utterance of call.transcription.content.utterances) {
      if (!utterance) {
        continue;
      }

      // Get speaker information if available
      const speaker = call.speakers?.get(utterance.channel);
      let speakerDisplay: string;

      if (speaker?.fullName) {
        speakerDisplay = speaker.fullName;
      } else if (speaker?.number) {
        speakerDisplay = formatInternational(speaker.number);
      } else if (mainSpeakers) {
        // Fallback: use participant info when speakers map is not available
        // For voicemails, always use the "from" (who left the message)
        const isVoicemail = call.type === "voicemail";

        const participant = isVoicemail
          ? call.from
          : utterance.channel === 0
            ? call.from
            : call.to;

        speakerDisplay =
          participant?.fullName ??
          formatInternational(participant?.number ?? "");
      } else {
        speakerDisplay = "Unknown Speaker";
      }

      result += `${formatSecondsToDuration(Math.round(utterance.startTime))} - ${speakerDisplay}\n`;
      result += `${utterance.entries.map((entry) => entry.W).join(" ")}\n\n`;
    }
  }

  return result.trim();
}

export function useDownloadTranscription(filename: string) {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const { call } = useCall();

  if (!call?.transcription) {
    return {
      downloadTranscription: noop,
    };
  }

  const downloadTranscription = () => {
    const parsedTranscription = parse(call, i18n.language);

    try {
      download(filename, parsedTranscription);

      showToast({
        variant: "success",
        dismissIn: 3000,
        message: t("toast.success"),
      });
    } catch (_) {
      showToast({
        variant: "critical",
        dismissIn: 3000,
        message: t("toast.download_failed"),
      });
    }
  };

  return {
    downloadTranscription,
  };
}
